import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { signal, useSignal } from "../net/signal";
import { clock, fa } from "./fa";
import { frontier, isOpen, loadBig, loadProgress, stageAt } from "./stages";
import { ANIMALS } from "./animals";
import { Thumb } from "./Thumb";

/**
 * مرحله‌های آفلاینِ فلش‌ها در پنلِ بازی (Hub) — نوارِ کشیدنیِ مرحله‌ها و دکمه‌ی شروع. مرحله‌ها بی‌پایان‌اند، پس
 * نوار فقط پنجره‌ای گردِ جایی که بازیکن رسیده را می‌کشد: چند مرحله‌ی تمام‌شده‌ی پیشین با ستاره‌هایشان، مرحله‌ی
 * فعلی، و چند مرحله‌ی بسته‌ی بعدی با قفل (نقشِ بسته پنهان است تا باز شدنش چیزی تازه نشان دهد).
 *
 * شروع را روی arrowsRun می‌گذارد و App ِ /play صفحه‌ی مرحله را تمام‌صفحه باز می‌کند.
 */
/** big: نقشه‌ی بزرگِ جانور (animals.ts) به جای مرحله. */
export const arrowsRun = signal<{ stage: number; seed: number; n: number; big?: string } | null>(null);

let runs = 0;
export function playStage(stage: number): void {
  arrowsRun.value = { stage, seed: 1 + Math.floor(Math.random() * 999_999_999), n: ++runs };
}

export function playBig(id: string): void {
  arrowsRun.value = { stage: -1, big: id, seed: 1 + Math.floor(Math.random() * 999_999_999), n: ++runs };
}

/** مرحله‌ای که همین حالا باز شد — بعد از برگشت به لابی با جهشی کوچک پیدا می‌شود. */
export const freshStage = signal<number | null>(null);

const BEHIND = 12, AHEAD = 6;

export function StageStrip() {
  const p = loadProgress();
  const reach = frontier(p);
  const [sel, setSel] = useState(reach);
  const strip = useRef<HTMLDivElement>(null);
  const fresh = useSignal(freshStage);
  const st = stageAt(sel);
  const best = p.best[String(sel)];
  const from = Math.max(0, Math.min(sel, reach) - BEHIND), to = reach + AHEAD;

  const [ends, setEnds] = useState({ start: false, end: false });
  /** کشیدنِ نوار با موش: کلیکِ بعد از کشیدن انتخاب نیست. */
  const dragged = useRef(false);

  // دکمه‌های دو سر فقط وقتی پیدایند که آن سو مرحله‌ی دیگری هست. scrollLeft ِ نوارِ راست‌به‌چپ منفی است.
  const measure = () => {
    const el = strip.current;
    if (!el) return;
    const x = Math.abs(el.scrollLeft), max = el.scrollWidth - el.clientWidth;
    setEnds({ start: x > 2, end: x < max - 2 });
  };

  // مرحله‌ی برگزیده همیشه وسطِ نوار است — بی scrollIntoView، که کلِ صفحه را هم جابه‌جا می‌کرد.
  useLayoutEffect(() => {
    const el = strip.current, t = el?.querySelector<HTMLElement>(".ar-tile.on");
    if (el && t) {
      const a = t.getBoundingClientRect(), b = el.getBoundingClientRect();
      el.scrollLeft += a.left + a.width / 2 - (b.left + b.width / 2);
    }
    measure();
  }, [sel]);

  /**
   * روی دسکتاپ نوارِ افقی با موش جابه‌جا نمی‌شد (کاربر، ۷ آبان: «مرحله‌ها قابلِ اسکرول و انتخاب نیستند»): چرخِ موش
   * صفحه را می‌بُرد و کشیدن هم کاری نمی‌کرد. حالا چرخ روی نوار نوار را می‌برد (تا ته، بعد دوباره صفحه را) و کشیدن با
   * موش هم؛ لمسِ گوشی همان کشیدنِ خودِ مرورگر است.
   */
  useEffect(() => {
    const el = strip.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return; // ترک‌پد خودش افقی می‌برد
      const max = el.scrollWidth - el.clientWidth, x = Math.abs(el.scrollLeft);
      // پایین = مرحله‌های بعد (سمتِ چپ در راست‌به‌چپ)
      if ((e.deltaY > 0 && x >= max - 1) || (e.deltaY < 0 && x <= 1)) return;
      e.preventDefault();
      el.scrollLeft -= e.deltaY;
    };
    let down: { x: number; left: number; id: number } | null = null;
    const pdown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      down = { x: e.clientX, left: el.scrollLeft, id: e.pointerId };
      dragged.current = false;
    };
    const pmove = (e: PointerEvent) => {
      if (!down || e.pointerId !== down.id) return;
      const dx = e.clientX - down.x;
      if (!dragged.current && Math.abs(dx) < 6) return;
      if (!dragged.current) { dragged.current = true; el.classList.add("dragging"); try { el.setPointerCapture(e.pointerId); } catch { /* بی گرفتن هم */ } }
      el.scrollLeft = down.left - dx;
    };
    const pup = () => { down = null; el.classList.remove("dragging"); };
    el.addEventListener("wheel", wheel, { passive: false });
    el.addEventListener("pointerdown", pdown);
    el.addEventListener("pointermove", pmove);
    el.addEventListener("pointerup", pup);
    el.addEventListener("pointercancel", pup);
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("wheel", wheel);
      el.removeEventListener("pointerdown", pdown);
      el.removeEventListener("pointermove", pmove);
      el.removeEventListener("pointerup", pup);
      el.removeEventListener("pointercancel", pup);
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, []);

  /** دکمه‌های دو سر: یک صفحه‌ی نوار به آن سو. dir ۱ = مرحله‌های پیشین (راست)، −۱ = بعدی (چپ). */
  const page = (dir: 1 | -1) => strip.current?.scrollBy({ left: dir * strip.current.clientWidth * 0.8, behavior: "smooth" });

  return (
    <section className="ar-stages" aria-label="مرحله‌ها">
      <div className="ar-stages-head">
        <b>{st.name}</b>
        <span>{fa(st.arrows)} فلش</span>
        {best !== undefined && <span>بهترین {clock(best)}</span>}
        <span className="ar-stages-count">مرحله‌ی {fa(sel + 1)}</span>
      </div>
      <div className="ar-stripwrap">
      {ends.start && (
        <button type="button" className="ar-stripnav start" onClick={() => page(1)} aria-label="مرحله‌های پیشین">
          <svg viewBox="0 0 24 24" aria-hidden><path d="M9 5l7 7-7 7" /></svg>
        </button>
      )}
      {ends.end && (
        <button type="button" className="ar-stripnav end" onClick={() => page(-1)} aria-label="مرحله‌های بعد">
          <svg viewBox="0 0 24 24" aria-hidden><path d="M15 5l-7 7 7 7" /></svg>
        </button>
      )}
      <div className="ar-strip" ref={strip} onClickCapture={(e) => { if (dragged.current) { e.stopPropagation(); e.preventDefault(); dragged.current = false; } }}>
        {Array.from({ length: to - from + 1 }, (_, k) => from + k).map((i) => {
          const open = isOpen(p, i);
          const won = i < p.level;
          return (
            <button key={i} type="button" disabled={!open} onClick={() => { setSel(i); freshStage.value = null; }} aria-pressed={i === sel}
              aria-label={open ? `مرحله‌ی ${fa(i + 1)}، ${stageAt(i).name}` : `مرحله‌ی ${fa(i + 1)}، بسته`}
              className={"ar-tile" + (i === sel ? " on" : "") + (won ? " won" : "") + (open ? "" : " locked") + (i === fresh ? " fresh" : "")}>
              {open ? <Thumb stage={i} tiled={won} /> : (
                <svg className="ar-lock" viewBox="0 0 24 24" aria-hidden>
                  <rect x="5" y="10.5" width="14" height="10" rx="2.5" />
                  <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
                </svg>
              )}
              <span className="ar-tile-n">{fa(i + 1)}</span>
              {won && (
                <span className="ar-tile-stars" aria-label={`${fa(p.stars[String(i)] ?? 1)} ستاره`}>
                  {[1, 2, 3].map((k) => (
                    <svg key={k} viewBox="0 0 24 24" className={k <= (p.stars[String(i)] ?? 1) ? "on" : ""} aria-hidden>
                      <path d="M12 2.5l2.9 5.9 6.6.9-4.8 4.6 1.2 6.5-5.9-3.1-5.9 3.1 1.2-6.5L2.5 9.3l6.6-.9z" />
                    </svg>
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>
      </div>
      <button type="button" className="btn bigbtn ar-go" onClick={() => playStage(sel)}>
        <svg viewBox="0 0 24 24" aria-hidden><path d="M8 5l11 7-11 7z" /></svg>
        شروع مرحله‌ی {fa(sel + 1)}
      </button>
    </section>
  );
}

/**
 * نقشه‌های بزرگِ جانوران (کاربر، ۸ آبان: عکس‌هایش «مپِ بازیِ فلش در وسعتِ بزرگ» شوند): شیر، اسب و غزال، همیشه باز. هر
 * کاشی طرحِ جانور و ستاره‌های بهترین بازی را دارد؛ لمس همان نقشه را تمام‌صفحه می‌آورد.
 */
export function BigMaps() {
  const p = loadBig();
  return (
    <section className="ar-bigmaps" aria-label="نقشه‌های بزرگ">
      <div className="ar-stages-head"><b>نقشه‌های بزرگ</b></div>
      <div className="ar-bigrow">
        {ANIMALS.map((a) => {
          const won = p[a.id];
          return (
            <button key={a.id} type="button" className={"ar-bigtile" + (won ? " won" : "")} onClick={() => playBig(a.id)}
              aria-label={`نقشه‌ی بزرگ ${a.name}`}>
              <Thumb animal={a.id} tiled={!!won} />
              <span className="ar-bigname">{a.name}</span>
              <span className="ar-tile-stars" aria-label={`${fa(won?.stars ?? 0)} ستاره`}>
                {[1, 2, 3].map((k) => (
                  <svg key={k} viewBox="0 0 24 24" className={k <= (won?.stars ?? 0) ? "on" : ""} aria-hidden>
                    <path d="M12 2.5l2.9 5.9 6.6.9-4.8 4.6 1.2 6.5-5.9-3.1-5.9 3.1 1.2-6.5L2.5 9.3l6.6-.9z" />
                  </svg>
                ))}
              </span>
              {won?.best !== undefined && <small className="ar-bigbest">{clock(won.best)}</small>}
            </button>
          );
        })}
      </div>
    </section>
  );
}
